import { useEffect, useState } from "react";
import { History, MessageCircle, Shield, Smartphone, UserRound } from "lucide-react";
import { MagicLinkPanel } from "@/components/profile/MagicLinkPanel";
import { ProfileSection } from "@/components/profile/ProfileSection";
import { SecurityLogsPanel } from "@/components/profile/SecurityLogsPanel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type MeTab = "profile" | "security" | "logs";

const tabs: { id: MeTab; label: string; icon: typeof UserRound }[] = [
  { id: "profile", label: "资料", icon: UserRound },
  { id: "security", label: "登录与安全", icon: Shield },
  { id: "logs", label: "日志", icon: History },
];

function PlaceholderCard({
  icon: Icon,
  title,
  description,
  hint,
}: {
  icon: typeof Shield;
  title: string;
  description: string;
  hint?: string;
}) {
  return (
    <section className="rounded-lg border border-dashed bg-muted/20 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0 space-y-1">
          <h2 className="text-base font-medium">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
      </div>
    </section>
  );
}

type MeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function MeDialog({ open, onOpenChange }: MeDialogProps) {
  const { user } = useAuth();
  const [tab, setTab] = useState<MeTab>("profile");

  useEffect(() => {
    if (!open) setTab("profile");
  }, [open]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(90vh,40rem)] w-[min(calc(100%-2rem),36rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
        <DialogHeader className="shrink-0 space-y-1 border-b border-border px-6 py-4 pr-14 text-left">
          <DialogTitle>个人中心</DialogTitle>
          <DialogDescription>管理展示名、头像、登录方式与账号日志。</DialogDescription>
        </DialogHeader>

        <div className="shrink-0 flex flex-wrap gap-2 border-b border-border px-6 py-3">
          {tabs.map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              type="button"
              variant={tab === id ? "default" : "outline"}
              size="sm"
              className={cn("gap-1.5")}
              onClick={() => setTab(id)}
            >
              <Icon className="size-4" />
              {label}
            </Button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 [scrollbar-gutter:stable]">
          {tab === "profile" ? <ProfileSection /> : null}

          {tab === "security" ? (
            <div className="space-y-4">
              <MagicLinkPanel />
              <PlaceholderCard
                icon={Smartphone}
                title="手机验证码"
                description="短信验证码登录与改绑即将支持。"
              />
              <PlaceholderCard
                icon={MessageCircle}
                title="微信登录"
                description="微信授权登录与绑定即将支持。"
              />
            </div>
          ) : null}

          {tab === "logs" ? <SecurityLogsPanel /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
